import './claim-detail-header.scss';

import { Box, Stack, Tooltip, Typography } from '@mui/material';
import { PaperLayout, PrimaryTitle, StatusBadge } from '../../../presentation';

import {
  Claimant,
  ClaimantType,
  ClaimContactType,
  ClaimStatus,
} from '@converge/graphql';

import { useTranslation } from 'react-i18next';
import {
  capitalizeFirstLetterRemoveUnderLine,
  formatDollarAmount,
} from '@converge/utils';
import { ClaimHandlerField } from '../claim/claim-entity-grid';
import { ConvergeNavigationUrls } from '../../../contexts';

const getClaimStatusColor = (status?: ClaimStatus): string => {
  switch (status) {
    case ClaimStatus.Open:
      return '#2E7D32'; // Green for open/reopened claims
    case ClaimStatus.Closed:
      return '#D32F2F'; // Red for closed claims
    case ClaimStatus.Reopened:
      return ' #0288d1';
    default:
      return '#2E7D32'; // Default to green
  }
};
const getClaimStatusBackgorundColor = (status?: ClaimStatus): string => {
  switch (status) {
    case ClaimStatus.Open:
      return '#EDF7ED'; // Green for open/reopened claims
    case ClaimStatus.Closed:
      return '#FDEDED'; // Red for closed claims
    case ClaimStatus.Reopened:
      return 'rgb(229, 246, 253)';
    default:
      return '#EDF7ED'; // Default to green
  }
};

interface IProps {
  claim: {
    dueDate?: string | null;
    uuid?: string;
    createdAt?: string;
    description?: string;
    discoveryDate?: string;
    documentLink?: string;
    jiraId?: string;
    referenceId?: string;
    reportedDate?: string;
    startDate?: string;
    status?: ClaimStatus;
    contacts?: ClaimContactType[];
    claimants?: Claimant[];
    tenant?: string;
    apeeLimit?: string;
    updatedAt?: string;
    insuredLogo?: string;
    insuredName?: string | null;
    insuredReferenceId?: string;
    insuredUuid?: string;
    policyReferenceId?: string;
    fallbackInsuredLogo?: string;
    brokerageName?: string | null;
    policy?: { referenceId?: string; type?: string };
    submissionUuid?: string;
    retention?: string;
    limit?: string;
    userFirstName?: string;
    userLastName?: string;
    userEmail?: string;
    userUuid?: string;
    submissionType?: string;
  };
}

const truncateText = (text: string, maxLength = 16): string => {
  if (!text) return text;
  return text.length > maxLength ? `${text.substring(0, maxLength)}...` : text;
};

export const ClaimDetailsHeader = ({ claim }: IProps) => {
  const { t } = useTranslation();

  const policyReferenceID = claim.policy?.referenceId;
  const isPrimaryPolicy = claim.policy?.type === 'PRIMARY';

  const tooltipStyles = {
    tooltip: {
      backgroundColor: '#333333',
      color: 'white',
      padding: '10px 20px',
      fontSize: '12px',
      textAlign: 'center',
      fontweight: '600',
    },
    arrow: {
      color: '#333333',
    },
  };

  const renderTooltipContent = (content: string) => {
    return (
      <Box sx={{ whiteSpace: 'pre-line', textAlign: 'center' }}>{content}</Box>
    );
  };

  const claimantsList =
    claim?.claimants
      ?.filter((claimant) => claimant.type === ClaimantType.Claimant)
      ?.slice(0, 3)
      .map((claimant) =>
        claimant.firstName || claimant.lastName
          ? `${claimant.firstName || ''} ${claimant.lastName || ''}`.trim()
          : claimant.companyName || ''
      )
      .filter((name) => name !== '') || [];

  const handlePolicyClick = () => {
    if (claim.documentLink) {
      window.open(`${claim.documentLink}/Policy`, '_blank');
    }
  };

  return (
    <PaperLayout className="entity-detail-header-layout">
      <div className="entity-detail-header-container">
        <div className="entity-reference-container">
          <div className="entity-id-container">
            <Typography variant="h5" className="entity-id">
              {claim.referenceId ?? 'C001-S493274778'}
            </Typography>
            <StatusBadge
              label={claim?.status?.toUpperCase() ?? 'OPEN'}
              color={getClaimStatusColor(claim?.status)}
              className="claim-status-badge"
              data-testid="status-badge"
              sx={{
                fontSize: '13px !important',
                fontWeight: '500 !important',
                marginLeft: '0px !important',
                padding: '2px 6px',
                height: '20px',
                borderRadius: '16px !important',
                textTransform: 'uppercase !important',
                backgroundColor: getClaimStatusBackgorundColor(claim?.status),
              }}
            />
          </div>
        </div>

        <ClaimDetailsHeader.Field
          label={t('underwriting.detail.header.policyType', 'POLICY')}
          value={capitalizeFirstLetterRemoveUnderLine(
            isPrimaryPolicy ? 'Primary' : 'Excess'
          )}
          subTitle={`${policyReferenceID ?? 'N/A'}`}
          tooltipValue={`${policyReferenceID ?? 'N/A'}`}
          onClick={handlePolicyClick}
          clickable={Boolean(claim.documentLink)}
          data-testid="policy-field"
        />

        <ClaimDetailsHeader.Field
          label={t('underwriting.detail.header.insuredLabel', 'INSURED')}
          value={truncateText(claim.insuredName ?? 'Private Tech, Inc')}
          tooltipValue={
            claim.insuredReferenceId
              ? `${claim.insuredName ?? 'Private Tech, Inc'}\n${
                  claim.insuredReferenceId
                }`
              : claim.insuredName ?? 'Private Tech, Inc'
          }
          subTitle={claim.insuredReferenceId ?? ''}
          onClick={() => {
            if (claim.insuredUuid) {
              window.open(
                ConvergeNavigationUrls.insuredEntityDetail({
                  insuredEntityUuid: claim.insuredUuid,
                  sectionUrl: 'overview',
                }),
                '_blank'
              );
            }
          }}
          clickable={Boolean(claim.insuredUuid && claim.insuredReferenceId)}
          data-testid="insured-field"
        />
        <Stack className="insured-entity-header-for-field">
          <Box style={{ paddingLeft: '30px', marginBottom: '4px' }}>
            <Typography variant="h6" margin={0} lineHeight={'1em'}>
              {claimantsList.length > 1
                ? t('underwriting.detail.header.claimantsLabel', 'CLAIMANTS')
                : t('underwriting.detail.header.claimantLabel', 'CLAIMANT')}
            </Typography>
          </Box>
          <Box>
            <Tooltip
              title={renderTooltipContent(claimantsList.join(', '))}
              placement="bottom-start"
              arrow
              slotProps={{
                tooltip: {
                  sx: tooltipStyles.tooltip,
                },
                arrow: {
                  sx: tooltipStyles.arrow,
                },
              }}
            >
              <Box style={{ paddingLeft: '30px' }}>
                {claimantsList.map((claimant, index) => (
                  <PrimaryTitle
                    key={index}
                    variant="h5"
                    margin={0}
                    lineHeight={'1em'}
                  >
                    {truncateText(claimant)}
                  </PrimaryTitle>
                ))}
              </Box>
            </Tooltip>
          </Box>
        </Stack>
        <ClaimDetailsHeader.Field
          label="AGGREGATE LIMIT"
          value={claim?.limit ? formatDollarAmount(+claim?.limit) : ''}
          alignCenter={true}
          paddingLeft={{ xs: '21px', lg: '40px' }}
        />
        <ClaimDetailsHeader.Field
          label="APEE"
          value={
            claim?.apeeLimit ? formatDollarAmount(+claim?.apeeLimit) : 'N/A'
          }
          alignCenter
          paddingRight={`${claim?.apeeLimit ? '42px' : '0px'}`}
          paddingLeft={`${claim?.apeeLimit ? '0px' : '3px'}`}
        />

        <ClaimDetailsHeader.Field
          label={isPrimaryPolicy ? 'RETENTION' : 'ATTACHMENT POINT'}
          value={claim?.retention ? formatDollarAmount(+claim?.retention) : ''}
          customWidth="80px"
          className={!isPrimaryPolicy ? 'attachment-point-field' : ''}
        />

        <Stack className="insured-entity-header-for-field claims-handler-field">
          <Box style={{ marginBottom: '6px' }}>
            <Typography variant="h6" margin={0} lineHeight={'1em'}>
              {t('claim.detail.header.claimsHandler', 'CLAIMS HANDLER')}
            </Typography>
          </Box>
          <Box>
            <ClaimHandlerField
              claimUuid={claim.uuid || ''}
              currentHandler={`${claim.userFirstName ?? 'John'} ${
                claim.userLastName ?? 'Spiehs'
              }`}
              userUuid={claim.userUuid}
            />
          </Box>
        </Stack>
      </div>
    </PaperLayout>
  );
};

ClaimDetailsHeader.Field = ({
  label,
  value,
  paddingRight,
  paddingLeft,
  tooltipValue,
  subTitle,
  onClick,
  clickable = false,
  alignCenter = false,
  className,
  'data-testid': dataTestId,
}: {
  label: string;
  value?: string;
  customWidth?: string;
  paddingLeft?:
    | string
    | { xs?: string; sm?: string; md?: string; lg?: string; xl?: string };
  tooltipValue?: string;
  subTitle?: string;
  onClick?: () => void;
  clickable?: boolean;
  paddingRight?:
    | string
    | { xs?: string; sm?: string; md?: string; lg?: string; xl?: string };
  alignCenter?: boolean;
  className?: string;
  'data-testid'?: string;
}) => {
  const displayValue = (
    <PrimaryTitle variant="h5" margin={0} lineHeight={'1em'}>
      {value}
    </PrimaryTitle>
  );

  const renderTooltipContent = (content: string) => {
    return (
      <Box sx={{ whiteSpace: 'pre-line', textAlign: 'center' }}>{content}</Box>
    );
  };

  const tooltipStyles = {
    tooltip: {
      backgroundColor: '#333333',
      color: 'white',
      padding: '10px 15px',
      fontSize: '12px !important',
      textAlign: 'center',
      fontweight: '600 !important',
    },
    arrow: {
      color: '#333333',
    },
  };

  return (
    <Stack
      className={`insured-entity-header-for-field ${className || ''}`}
      style={{
        minWidth: '120px',
      }}
      sx={{
        alignItems: alignCenter ? 'center' : 'flex-start',
      }}
      data-testid={dataTestId}
    >
      <Box>
        <Typography
          variant="h6"
          margin={0}
          lineHeight={'1em'}
          sx={{
            paddingLeft: paddingLeft || '',
            paddingRight: paddingRight || '',
            marginBottom: '4px',
          }}
        >
          {label}
        </Typography>
      </Box>
      <Box>
        {tooltipValue ? (
          <Tooltip
            title={renderTooltipContent(tooltipValue)}
            placement="bottom-start"
            arrow
            slotProps={{
              tooltip: {
                sx: tooltipStyles.tooltip,
              },
              arrow: {
                sx: tooltipStyles.arrow,
              },
            }}
          >
            <Box>
              {displayValue}
              {subTitle && (
                <PrimaryTitle
                  sx={{
                    fontWeight: '600 !important',
                    textDecoration: clickable ? 'underline' : 'none',
                    color: clickable ? 'inherit' : 'inherit',
                    cursor: clickable ? 'pointer' : 'default',
                    '&:hover': clickable
                      ? {
                          color: 'primary.dark',
                        }
                      : {},
                  }}
                  variant="h5"
                  margin={0}
                  lineHeight={'1em'}
                  onClick={clickable && onClick ? onClick : undefined}
                >
                  {truncateText(subTitle)}
                </PrimaryTitle>
              )}
            </Box>
          </Tooltip>
        ) : (
          <>
            {displayValue}
            {subTitle && (
              <PrimaryTitle
                sx={{
                  fontWeight: '600 !important',
                  textDecoration: clickable ? 'underline' : 'none',
                  color: clickable ? 'inherit' : 'inherit',
                  cursor: clickable ? 'pointer' : 'default',
                  '&:hover': clickable
                    ? {
                        color: 'primary.dark',
                      }
                    : {},
                }}
                variant="h5"
                margin={0}
                lineHeight={'1em'}
                onClick={clickable && onClick ? onClick : undefined}
              >
                {truncateText(subTitle)}
              </PrimaryTitle>
            )}
          </>
        )}
      </Box>
    </Stack>
  );
};
